import { FC } from "react";
import { Flipped } from "react-flip-toolkit";
import { makeStyles } from "tss-react/mui";
import { MiIcon } from "@miplatform/components";
import { POPPER_DELAY, TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiMinusCircle,
  mdiPlusCircle,
} from "@mdi/js";
import { Box, Tooltip, Typography } from "@mui/material";
import { ITransferListItemProps } from "../TransferList.props";

export const TransferListItem: FC<ITransferListItemProps> = ({
  item,
  renderItemTitle,
  onAddItem,
  onRemoveItem,
  itemCount,
  allowResorting,
  onReorderUp,
  onReorderDown,
  index,
  customAction,
  addDisabled,
}) => {
  const isAddDisabled = addDisabled || Boolean(item.disabled);
  const isFirstInList = index === 0;
  const isLastInList = index === itemCount - 1;
  const { classes, theme } = useStyles({
    hasItems: itemCount > 1,
    disabled: (!onAddItem && !onRemoveItem) || isAddDisabled,
  });

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAddItem && !isAddDisabled) {
      onAddItem(item.key);
    }
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemoveItem) {
      onRemoveItem(item.key);
    }
  };

  const handleUpClick = () => {
    if (onReorderUp && itemCount > 1) {
      onReorderUp(item.key);
    }
  };

  const handleDownClick = () => {
    if (onReorderDown && itemCount > 1) {
      onReorderDown(item.key);
    }
  };

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onAddItem && !isAddDisabled) {
      e.stopPropagation();
      onAddItem(item.key);
    } else if (onRemoveItem) {
      e.stopPropagation();
      onRemoveItem(item.key);
    }
  };

  const transferListCard = (
    <Box className={classes.transferListItemCard} onClick={handleCardClick}>
      <Box className={classes.transferListItemCardContent}>
        <Box className={classes.transferListItemCardText}>
          {renderItemTitle ? (
            renderItemTitle(item)
          ) : (
            <Typography
              variant={TYPOGRAPHY_VARIANTS.BD2}
              className={classes.transferListItemText}
            >
              {item.title}{" "}
              {item.description && (
                <Typography
                  variant={TYPOGRAPHY_VARIANTS.BD2}
                  color="textSecondary"
                  component="span"
                >
                  {item.description}
                </Typography>
              )}
            </Typography>
          )}
        </Box>
        {customAction && customAction(item, index)}
        <Box className={classes.iconContainer}>
          {onAddItem && (
            <MiIcon
              path={item.addIcon ?? mdiPlusCircle}
              color={theme.palette.primary.medium}
              onClick={handleAddClick}
              className={classes.icon}
              size={0.75}
              disabled={isAddDisabled}
            />
          )}
          {onRemoveItem && (
            <MiIcon
              path={item.removeIcon ?? mdiMinusCircle}
              color={theme.palette.error.main}
              onClick={handleRemoveClick}
              className={classes.icon}
              size={0.75}
            />
          )}
        </Box>
      </Box>
    </Box>
  );

  const tooltipTitle = (
    <Box>
      {item.description && (
        <Typography variant={TYPOGRAPHY_VARIANTS.BD2} component="p">
          {item.description}
        </Typography>
      )}
      {item.disabledReason && (
        <Typography
          variant={TYPOGRAPHY_VARIANTS.BD2}
          sx={{ color: "warning.main" }}
          component="p"
        >
          {item.disabledReason}
        </Typography>
      )}
    </Box>
  );

  return (
    <Flipped flipId={item.key}>
      <Box className={classes.transferListItemExternalContainer}>
        {item.description || item.disabledReason ? (
          <Tooltip
            enterDelay={POPPER_DELAY}
            enterNextDelay={POPPER_DELAY}
            title={tooltipTitle}
            placement="top-start"
            arrow
          >
            <Box className={classes.tooltipWrapper}>{transferListCard}</Box>
          </Tooltip>
        ) : (
          transferListCard
        )}
        {allowResorting && (
          <Box className={classes.arrowsContainer}>
            <MiIcon
              path={mdiChevronUp}
              color={theme.palette.grey[700]}
              onClick={handleUpClick}
              disabled={isFirstInList}
              size={0.75}
            />
            <MiIcon
              path={mdiChevronDown}
              color={theme.palette.grey[700]}
              onClick={handleDownClick}
              disabled={isLastInList}
              size={0.75}
            />
          </Box>
        )}
      </Box>
    </Flipped>
  );
};

const useStyles = makeStyles<{
  hasItems: boolean;
  disabled: boolean;
}>()((theme, { hasItems, disabled }) => ({
  transferListItemExternalContainer: {
    display: "flex",
    flex: 1,
    flexDirection: "row",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  transferListItemCard: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(0, 2),
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(1),
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    backgroundColor: theme.palette.common.white,
    minHeight: 40,
    boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.12)",
    overflow: "hidden",
    cursor: disabled ? "default" : "pointer",
  },
  tooltipWrapper: {
    display: "flex",
    flex: 1,
    minWidth: 0,
  },
  transferListItemCardContent: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    minWidth: 0,
    width: "100%",
  },
  transferListItemCardText: {
    display: "flex",
    gap: theme.spacing(1),
    minWidth: 0,
    flex: 1,
  },
  transferListItemText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  arrowsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    opacity: hasItems ? 1 : 0.5,
  },
  icon: {
    position: "absolute",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    margin: "auto",
  },
  iconContainer: {
    width: 24,
    height: 24,
    position: "relative",
  },
}));
